import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'
import { DisconnectionDetails, useConversation } from '@elevenlabs/react'
import { Orb, OrbStateBadge, type AgentState } from '@/uiV2/elevenLabs/orb'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import { Pages } from '../../../Pages'
import Button from '../../common/form/inputs/Button'
import { jsCapture, jsIdentify } from '../../../jobseeker/jobseekerPosthog'
import authApi from '@/api/auth/AuthApi'
import kitApi from '@/api/kit/KitApi'
import { PersonDto } from '@/api/person/models/PersonDto'
import type { PersonNetworkImportStatusSummaryDto } from '@/api/person/PersonApi'
import ShareInviteCard from '../components/ShareInviteCard'
import SpeakToKitLogo from '../components/SpeakToKitLogo'
import { useShareInvite } from '../hooks/useShareInvite'
import {
    speakToKitSerifStyle,
    speakToKitThemeVars,
} from '../theme/speakToKitThemeVars'
import useSpeakToKitPageTitle from '../hooks/useSpeakToKitPageTitle'

const containerClass = 'mx-auto w-full max-w-[1200px] px-6 font-sans'

export default function CandidateApplyKit(): React.JSX.Element | null {
    useSpeakToKitPageTitle('Call with Kit')
    const navigate = useNavigate()
    const { jobUrlId: jobUrlIdParam } = useParams()
    const jobUrlId = (jobUrlIdParam ?? '').trim()
    const [error, setError] = useState<string | null>(null)
    const [isStarting, setIsStarting] = useState(false)
    const [isStopping, setIsStopping] = useState(false)
    const [conversationMode, setConversationMode] = useState<
        'speaking' | 'listening' | null
    >(null)
    const [hasAutoStarted, setHasAutoStarted] = useState(false)
    const [hasConnectedOnce, setHasConnectedOnce] = useState(false)
    const [showThankYou, setShowThankYou] = useState(false)
    const hasConnectedOnceRef = useRef(false)
    const connectStartTimeRef = useRef<number | null>(null)
    const callStartTimeRef = useRef<number | null>(null)
    const conversationIdRef = useRef<string | null>(null)
    const hasCallStartedRef = useRef(false)
    const identifiedUserRef = useRef<string | null>(null)
    const [sessionInfo, setSessionInfo] = useState<{
        user: { id: string; email: string; name: string; referralCode?: string }
        person_profile: PersonDto | null
        personNetworkImportStatus: PersonNetworkImportStatusSummaryDto | null
    } | null>(null)
    const [loadingSession, setLoadingSession] = useState(true)
    const [jobDetails, setJobDetails] = useState<{
        jobUrlId: string
        jobTitle: string
        agentId: string
        isActive: boolean
    } | null>(null)
    const [jobLoadState, setJobLoadState] = useState<
        'loading' | 'ready' | 'inactive' | 'not_found' | 'error'
    >('loading')
    const [jobLoadError, setJobLoadError] = useState<string | null>(null)

    const userId = sessionInfo?.user.id ?? ''
    const userEmail = sessionInfo?.user.email ?? ''
    const name = sessionInfo?.user.name ?? ''
    const referralCode = sessionInfo?.user.referralCode?.trim() ?? ''
    const agentId = jobDetails?.agentId ?? ''
    const displayJobTitle = jobDetails?.jobTitle || 'this role'
    const shouldShowNotFound = !jobUrlId || jobLoadState === 'not_found'
    const buildShareInviteMessage = useCallback((shareUrl: string) => {
        return `I just spoke to Kit. It's a talent agent designed to work for you and get past the usual CV filtering tools. Very good. You might find it helpful too.\n\n${shareUrl}`
    }, [])
    const trackInviteCopied = useCallback(() => {
        jsCapture('jobseeker_apply_kit_url_copied')
    }, [])
    const trackInviteShared = useCallback(() => {
        jsCapture('jobseeker_apply_kit_share_clicked')
    }, [])

    const personalizeShareUrl = useCallback(
        (baseUrl: string) => {
            if (!referralCode) {
                return baseUrl
            }
            try {
                const origin =
                    typeof window !== 'undefined'
                        ? window.location?.origin
                        : undefined
                const url = new URL(
                    baseUrl,
                    baseUrl.startsWith('http') ? undefined : origin,
                )
                url.searchParams.set('ref', referralCode)
                return url.toString()
            } catch {
                const separator = baseUrl.includes('?') ? '&' : '?'
                return `${baseUrl}${separator}ref=${encodeURIComponent(referralCode)}`
            }
        },
        [referralCode],
    )

    const {
        shareInviteUrl,
        whatsAppShareUrl,
        isMobileShareAvailable,
        isDesktopViewport,
        hasCopiedLink,
        handleCopyInvite,
        handleShareInvite,
    } = useShareInvite({
        landingPath: Pages.CandidateLandingOption2,
        messageBuilder: buildShareInviteMessage,
        onCopy: trackInviteCopied,
        onShare: trackInviteShared,
        transformShareUrl: personalizeShareUrl,
    })
    const brandLogo = (
        <SpeakToKitLogo
            to={Pages.CandidateLandingOption2}
            ariaLabel="Speak To Kit home"
        />
    )

    const { startSession, endSession, status, getOutputVolume } =
        useConversation({
            onConnect: ({ conversationId }) => {
                setHasConnectedOnce(true)
                hasConnectedOnceRef.current = true
                setShowThankYou(false)
                setError(null)
                conversationIdRef.current = conversationId || null

                const connectStart = connectStartTimeRef.current
                const connectTimeMs =
                    typeof connectStart === 'number'
                        ? Math.max(0, Date.now() - connectStart)
                        : undefined

                jsCapture('jobseeker_apply_kit_connected', {
                    agent_id: agentId,
                    connect_time_ms: connectTimeMs,
                })

                if (!hasCallStartedRef.current) {
                    callStartTimeRef.current = Date.now()
                    hasCallStartedRef.current = true
                    jsCapture('jobseeker_apply_kit_started', {
                        call_id: conversationIdRef.current ?? undefined,
                    })
                }
            },
            onDisconnect: (details: DisconnectionDetails) => {
                const disconnectMessage =
                    details?.reason === 'error'
                        ? details?.context instanceof CloseEvent
                            ? details.context.reason ||
                              'Unable to connect to Kit.'
                            : 'Unable to connect to Kit.'
                        : null
                const endedConversationId =
                    conversationIdRef.current?.trim() || null

                if (endedConversationId) {
                    void kitApi
                        .markConvoCallEnded({
                            convId: endedConversationId,
                        })
                        .catch((kitError) => {
                            console.error(
                                'Failed to mark Kit conversation as call ended for screening job',
                                {
                                    convId: endedConversationId,
                                    kitError,
                                },
                            )
                        })
                }

                if (!hasConnectedOnceRef.current && disconnectMessage) {
                    setError(disconnectMessage)
                }
                if (hasConnectedOnceRef.current) {
                    setShowThankYou(true)
                }

                const durationSeconds =
                    callStartTimeRef.current !== null
                        ? Math.max(
                              0,
                              Math.round(
                                  (Date.now() - callStartTimeRef.current) /
                                      1000,
                              ),
                          )
                        : undefined
                const endReason =
                    details?.reason === 'user'
                        ? 'hangup_by_user'
                        : details?.reason === 'agent'
                          ? 'agent_finished'
                          : details?.reason === 'error'
                            ? 'error'
                            : 'disconnect'

                if (hasCallStartedRef.current) {
                    jsCapture('jobseeker_apply_kit_call_ended', {
                        call_id: conversationIdRef.current ?? undefined,
                        duration_s: durationSeconds,
                        end_reason: endReason,
                        completed: details?.reason === 'agent',
                    })
                    hasCallStartedRef.current = false
                    callStartTimeRef.current = null
                }

                setConversationMode(null)
            },
            onModeChange: ({ mode }) => {
                setConversationMode(mode)
            },
            onError: (message) => {
                setError(message || 'Something went wrong starting the call.')
            },
        })

    useEffect(() => {
        let active = true

        const redirectToLogin = (email?: string) => {
            const target = jobUrlId
                ? `${Pages.CandidateApply}/${jobUrlId}/kit`
                : Pages.CandidateApply
            const params = new URLSearchParams()
            if (email) {
                params.set('email', email)
                params.set('step', 'choose-method')
            }
            params.set('target', target)
            navigate(`${Pages.CandidateLogin}?${params.toString()}`, {
                replace: true,
            })
        }

        const loadSessionUser = async () => {
            try {
                const res = await authApi.getMeDetails()
                if (!active) return

                if (!res.user) {
                    redirectToLogin()
                    return
                }

                setSessionInfo({
                    user: {
                        id: res.user.id,
                        email: res.user.email,
                        name: res.user.name?.trim() || res.user.email,
                        referralCode: res.user.referralCode,
                    },
                    person_profile: res.person ?? null,
                    personNetworkImportStatus:
                        res.personNetworkImportStatus ?? null,
                })
            } catch (err) {
                console.error('Failed to load job seeker session', err)
                if (active) {
                    redirectToLogin()
                }
            } finally {
                if (active) {
                    setLoadingSession(false)
                }
            }
        }

        loadSessionUser()

        return () => {
            active = false
        }
    }, [jobUrlId, navigate])

    useEffect(() => {
        if (!sessionInfo?.user) return
        if (!jobUrlId) {
            setJobDetails(null)
            setJobLoadState('not_found')
            return
        }

        let cancelled = false

        const loadJobDetails = async () => {
            setJobLoadState('loading')
            setJobLoadError(null)
            try {
                const res = await kitApi.getScreeningJob(jobUrlId)
                if (cancelled) return
                if (!res.job) {
                    setJobDetails(null)
                    setJobLoadState('not_found')
                    return
                }

                setJobDetails({
                    jobUrlId,
                    jobTitle: res.job.jobTitle?.trim() || 'This role',
                    agentId: res.job.agentId,
                    isActive: res.job.isActive,
                })

                if (!res.job.isActive) {
                    setJobLoadState('inactive')
                    return
                }

                setJobLoadState('ready')
            } catch (err) {
                console.error('Failed to load screening job', err)
                if (!cancelled) {
                    setJobDetails(null)
                    setJobLoadState('error')
                    setJobLoadError(
                        err instanceof Error
                            ? err.message
                            : 'Unable to load this role right now.',
                    )
                }
            }
        }

        loadJobDetails()

        return () => {
            cancelled = true
        }
    }, [jobUrlId, sessionInfo])

    useEffect(() => {
        if (loadingSession) return
        if (jobLoadState !== 'ready') return
        jsCapture('jobseeker_apply_kit_viewed')
        // jsCapture is a module import and stable
    }, [jobLoadState, loadingSession])

    useEffect(() => {
        if (jobLoadState !== 'ready') return
        if (!userId) return
        if (identifiedUserRef.current === userId) return

        jsIdentify(userId, userEmail ? { email: userEmail } : undefined)
        identifiedUserRef.current = userId
    }, [jobLoadState, userEmail, userId])

    useEffect(() => {
        hasConnectedOnceRef.current = hasConnectedOnce
    }, [hasConnectedOnce])

    useEffect(() => {
        if (status === 'disconnected' && hasConnectedOnce) {
            setShowThankYou(true)
        }
    }, [hasConnectedOnce, status])

    useEffect(() => {
        return () => {
            endSession().catch((endError) => {
                console.error('Failed to end ElevenLabs session on unmount', {
                    endError,
                })
            })
        }
        // We only want to run this cleanup on unmount; dependency here would
        // cause the cleanup to fire on every render if endSession changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleStart = useCallback(async () => {
        if (!userId || !name || !agentId || jobLoadState !== 'ready') return

        setIsStarting(true)
        setError(null)
        setShowThankYou(false)
        hasCallStartedRef.current = false
        connectStartTimeRef.current = Date.now()
        callStartTimeRef.current = null
        setConversationMode(null)
        conversationIdRef.current = null

        jsCapture('jobseeker_apply_kit_connect_attempted', {
            agent_id: agentId,
        })

        try {
            await navigator.mediaDevices.getUserMedia({ audio: true })
        } catch (permissionError) {
            console.error(
                'Microphone permission was denied for ElevenLabs conversation',
                permissionError,
            )
            setError(
                'We need permission to use your microphone before starting the call.',
            )
            setIsStarting(false)
            return
        }

        try {
            const personImportStatus =
                sessionInfo?.personNetworkImportStatus ?? null
            const personImportCompleted =
                personImportStatus?.status === 'COMPLETE'

            const dynamicVariables: Record<string, string | boolean> = {
                user_id: userId,
                name,
                job_url_id: jobUrlId,
                person_import_complete: personImportCompleted,
            }

            if (sessionInfo?.person_profile) {
                dynamicVariables.person_profile = JSON.stringify(
                    sessionInfo.person_profile,
                )
            }

            if (
                !personImportCompleted &&
                personImportStatus?.status === 'IN_PROGRESS' &&
                personImportStatus.statusId
            ) {
                dynamicVariables.person_import_id = personImportStatus.statusId
            }
            const conversationId = await startSession({
                agentId,
                connectionType: 'webrtc',
                userId,
                dynamicVariables,
            })
            if (conversationId) {
                conversationIdRef.current = conversationId
                void kitApi
                    .startConvoSelf({
                        convId: conversationId,
                        personId: sessionInfo?.person_profile?.id,
                        dynamicVariables,
                        agentId,
                        source: jobUrlId
                            ? `job_screening_kit:${jobUrlId}`
                            : undefined,
                    })
                    .catch((kitError) => {
                        console.error(
                            'Failed to log Kit conversation for screening job',
                            kitError,
                        )
                    })
            }
        } catch (startError) {
            console.error('Failed to start ElevenLabs conversation', startError)
            const message =
                startError instanceof Error
                    ? startError.message
                    : 'Unable to start the call right now.'
            setError(message)
        } finally {
            setIsStarting(false)
        }
        // jsCapture is stable
    }, [
        agentId,
        jobLoadState,
        jobUrlId,
        name,
        sessionInfo,
        startSession,
        userId,
    ])

    useEffect(() => {
        if (hasAutoStarted) return
        if (!userId || !name || !agentId || jobLoadState !== 'ready') return
        if (showThankYou) return
        if (status !== 'disconnected') return

        setHasAutoStarted(true)
        void handleStart()
    }, [
        agentId,
        handleStart,
        hasAutoStarted,
        jobLoadState,
        name,
        showThankYou,
        status,
        userId,
    ])

    const handleEnd = useCallback(async () => {
        setIsStopping(true)
        setError(null)
        setShowThankYou(false)

        try {
            await endSession()
        } catch (stopError) {
            console.error('Failed to end ElevenLabs conversation', stopError)
            const message =
                stopError instanceof Error
                    ? stopError.message
                    : 'Unable to end the call. Please try again.'
            setError(message)
        } finally {
            setIsStopping(false)
        }
    }, [endSession])

    const agentState: AgentState = useMemo(() => {
        if (status !== 'connected') return null
        if (conversationMode === 'speaking') return 'talking'
        if (conversationMode === 'listening') return 'listening'
        return null
    }, [conversationMode, status])
    const isAutoConnecting =
        !hasAutoStarted &&
        Boolean(userId && name && agentId) &&
        jobLoadState === 'ready' &&
        !showThankYou &&
        status === 'disconnected'
    const isConnecting = status === 'connecting' || isStarting
    const isActiveCall =
        status === 'connecting' ||
        status === 'connected' ||
        status === 'disconnecting' ||
        isStarting ||
        isAutoConnecting
    const canStart =
        status === 'disconnected' &&
        !isStarting &&
        !showThankYou &&
        Boolean(agentId) &&
        jobLoadState === 'ready'
    const shouldShowStartButton =
        !isActiveCall && !showThankYou && (hasAutoStarted || Boolean(error))
    const showHangUp = status === 'connected' || status === 'disconnecting'
    const canHangUp = status === 'connected' && !isStopping
    const shouldShowOrb = isActiveCall

    if (loadingSession || jobLoadState === 'loading') {
        return (
            <div
                className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
                style={speakToKitThemeVars}
            >
                <header className="w-full py-6">
                    <div
                        className={`${containerClass} flex items-center justify-center`}
                    >
                        {brandLogo}
                    </div>
                </header>
                <main className="flex-1">
                    <section className="py-8 md:py-12">
                        <div className={containerClass}>
                            <div className="mx-auto w-full max-w-2xl">
                                <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                                    <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                        Loading role details…
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        )
    }

    if (jobLoadState === 'error') {
        return (
            <div
                className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
                style={speakToKitThemeVars}
            >
                <header className="w-full py-6">
                    <div
                        className={`${containerClass} flex items-center justify-center`}
                    >
                        {brandLogo}
                    </div>
                </header>

                <main className="flex-1">
                    <section className="py-8 md:py-12">
                        <div className={containerClass}>
                            <div className="mx-auto w-full max-w-2xl">
                                <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                                    <h2
                                        className="text-2xl font-semibold text-foreground"
                                        style={speakToKitSerifStyle}
                                    >
                                        Unable to load this role
                                    </h2>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        We&apos;re having trouble loading the
                                        job details. Please refresh and try
                                        again.
                                    </p>
                                    {jobLoadError ? (
                                        <p className="mt-3 text-xs text-muted-foreground">
                                            Error: {jobLoadError}
                                        </p>
                                    ) : null}
                                    <div className="mt-5 flex justify-center">
                                        <Button
                                            type="button"
                                            style="secondary"
                                            onClick={() => {
                                                window.location.reload()
                                            }}
                                            classes="border border-primary/40 text-primary px-6 py-2 hover:bg-primary/10"
                                        >
                                            Try again
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        )
    }

    if (jobLoadState === 'inactive') {
        return (
            <div
                className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
                style={speakToKitThemeVars}
            >
                <header className="w-full py-6">
                    <div
                        className={`${containerClass} flex items-center justify-center`}
                    >
                        {brandLogo}
                    </div>
                </header>

                <main className="flex-1">
                    <section className="py-8 md:py-12">
                        <div className={containerClass}>
                            <div className="mx-auto w-full max-w-2xl">
                                <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                                        Inactive
                                    </p>
                                    <h2
                                        className="mt-3 text-2xl font-semibold text-foreground"
                                        style={speakToKitSerifStyle}
                                    >
                                        This role is no longer active
                                    </h2>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        This screening link is no longer
                                        accepting new applicants.
                                    </p>
                                    <div className="mt-5 flex justify-center">
                                        <Button
                                            type="link"
                                            style="secondary"
                                            to={Pages.CandidateLandingOption2}
                                            classes="border border-primary/40 text-primary px-6 py-2 hover:bg-primary/10"
                                        >
                                            Back to Speak To Kit
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        )
    }

    if (shouldShowNotFound) {
        return (
            <div
                className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
                style={speakToKitThemeVars}
            >
                <header className="w-full py-6">
                    <div
                        className={`${containerClass} flex items-center justify-center`}
                    >
                        {brandLogo}
                    </div>
                </header>

                <main className="flex-1">
                    <section className="py-8 md:py-12">
                        <div className={containerClass}>
                            <div className="mx-auto w-full max-w-2xl">
                                <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-[var(--shadow-card)] md:p-8">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                                        404
                                    </p>
                                    <h2
                                        className="mt-3 text-2xl font-semibold text-foreground"
                                        style={speakToKitSerifStyle}
                                    >
                                        Kit link not found
                                    </h2>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        We couldn&apos;t find this Kit link.
                                        Please check the URL or contact the
                                        person who shared it.
                                    </p>
                                    <div className="mt-5 flex justify-center">
                                        <Button
                                            type="link"
                                            style="secondary"
                                            to={Pages.CandidateLandingOption2}
                                            classes="border border-primary/40 text-primary px-6 py-2 hover:bg-primary/10"
                                        >
                                            Back to Speak To Kit
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        )
    }

    if (!sessionInfo?.user) {
        return null
    }

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={speakToKitThemeVars}
        >
            <header className="w-full py-6">
                <div
                    className={`${containerClass} flex items-center justify-center`}
                >
                    {brandLogo}
                </div>
            </header>

            <main className="flex-1">
                <section className="py-8 md:py-12">
                    <div className={containerClass}>
                        <div className="mx-auto flex w-full max-w-3xl flex-col space-y-8 lg:space-y-10">
                            <div className="rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                                <h1
                                    className="text-2xl font-semibold text-foreground"
                                    style={speakToKitSerifStyle}
                                >
                                    {displayJobTitle}
                                </h1>
                            </div>
                            {showThankYou ? (
                                <ShareInviteCard
                                    shareInviteUrl={shareInviteUrl}
                                    whatsAppShareUrl={whatsAppShareUrl}
                                    isMobileShareAvailable={
                                        isMobileShareAvailable
                                    }
                                    isDesktopViewport={isDesktopViewport}
                                    hasCopiedLink={hasCopiedLink}
                                    onCopy={handleCopyInvite}
                                    onShare={handleShareInvite}
                                />
                            ) : null}

                            {!showThankYou || isActiveCall ? (
                                <div className="relative rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)]">
                                    <div className="space-y-4">
                                        {error && !hasConnectedOnce ? (
                                            <Note
                                                type={NoteType.ERROR}
                                                containerClassNames="w-full border border-red-100 bg-red-50/80"
                                            >
                                                {error}
                                            </Note>
                                        ) : null}

                                        {shouldShowStartButton ? (
                                            <Button
                                                type="button"
                                                style="primary"
                                                enabled={canStart}
                                                classes="w-full justify-center gap-2 py-3 text-base bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:bg-primary/90 transition-all focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2"
                                                onClick={handleStart}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faPhone}
                                                />
                                                {isConnecting
                                                    ? 'Connecting…'
                                                    : 'Speak to Kit'}
                                            </Button>
                                        ) : null}

                                        {shouldShowOrb ? (
                                            <div className="flex w-full flex-col items-center gap-4">
                                                <div className="w-full rounded-2xl border border-border bg-secondary/50 p-4">
                                                    <div className="mx-auto aspect-square max-w-48">
                                                        {status ===
                                                        'connected' ? (
                                                            <Orb
                                                                className="h-full w-full"
                                                                agentState={
                                                                    agentState
                                                                }
                                                                volumeMode={
                                                                    status ===
                                                                    'connected'
                                                                        ? 'manual'
                                                                        : 'auto'
                                                                }
                                                                getOutputVolume={
                                                                    status ===
                                                                    'connected'
                                                                        ? getOutputVolume
                                                                        : undefined
                                                                }
                                                            />
                                                        ) : (
                                                            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                                                                Connecting to
                                                                Kit…
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <OrbStateBadge
                                                    agentState={agentState}
                                                />
                                                {showHangUp ? (
                                                    <Button
                                                        type="button"
                                                        style="mild-danger"
                                                        enabled={canHangUp}
                                                        classes="min-w-[140px]"
                                                        onClick={handleEnd}
                                                    >
                                                        {isStopping ||
                                                        status ===
                                                            'disconnecting'
                                                            ? 'Ending…'
                                                            : 'Hang up'}
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        {!shouldShowOrb && isActiveCall ? (
                                            <p className="text-sm text-muted-foreground">
                                                Connecting to Kit…
                                            </p>
                                        ) : null}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </section>
            </main>
        </div>
    )
}
